import React, { useState, useEffect } from 'react';
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { toast } from "@/hooks/use-toast";
import {
  Sparkles,
  AlertTriangle,
  TrendingUp,
  CheckCircle2,
  ChevronDown,
  RefreshCw,
  Info,
  Zap,
  Target,
  Shield,
} from 'lucide-react';

/**
 * ExecutiveAssist Drawer
 *
 * AI-powered executive insights panel that opens from the top bar.
 * Gathers context from the current view and generates insights.
 *
 * Checkpoint 3.4C: the trigger is icon-only (no "ExecutiveAssist"/"AI"
 * text pill) so it sits cleanly in the global utility cluster next to the
 * notification bell — same h-10 w-10 rounded-full circular treatment as
 * notification-bell/user-menu-trigger in AppShell.jsx.
 *
 * Checkpoint 3.4D: dropped the purple/indigo tint (ring + icon color) that
 * 3.4C kept as "the only visual distinction" — real Emergent preview
 * showed that accent read as an unwanted indigo halo around the button.
 * The trigger is now the exact same neutral treatment as
 * notification-bell/user-menu-trigger (bg-white/10, ring-white/10,
 * text-slate-100); the Sparkles glyph alone identifies the feature, the
 * surrounding button no longer needs its own accent. The drawer's own
 * contents (unaffected by this button styling) are unchanged.
 */
const ExecutiveAssistDrawer = ({ 
  period = "mtd",
  vertical,
  metaContext,  // {level, campaign_id, adset_id, ad_id}
  className = ""
}) => {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [insights, setInsights] = useState(null);
  const [error, setError] = useState(null);

  // Generate insights when drawer opens
  const generateInsights = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const payload = {
        period,
        vertical: vertical && vertical !== "all" ? vertical : null,
        meta_context: metaContext || null,
      };
      
      const res = await api.post('/executive-assist/insights', payload);
      setInsights(res.data);
    } catch (err) {
      const message = err.response?.data?.detail || "Failed to generate insights";
      setError(message);
      toast({
        title: "ExecutiveAssist Error",
        description: message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Auto-generate on open
  useEffect(() => {
    if (open && !insights && !loading) {
      generateInsights();
    }
  }, [open]);

  const getSeverityColor = (severity) => {
    switch (severity?.toLowerCase()) {
      case 'high': return 'bg-red-500/20 text-red-300 border-red-500/30';
      case 'medium': return 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30';
      case 'low': return 'bg-blue-500/20 text-blue-300 border-blue-500/30';
      default: return 'bg-slate-500/20 text-slate-300 border-slate-500/30';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'immediate': return 'bg-red-500/20 text-red-300';
      case 'short-term': return 'bg-yellow-500/20 text-yellow-300';
      case 'medium-term': return 'bg-blue-500/20 text-blue-300';
      default: return 'bg-slate-500/20 text-slate-300';
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button
          variant="secondary"
          className={`h-10 w-10 rounded-full bg-white/10 p-0 text-slate-100 ring-1 ring-white/10 hover:bg-white/15 ${className}`}
          data-testid="executive-assist-trigger"
          aria-label="ExecutiveAssist"
          title="ExecutiveAssist"
        >
          <Sparkles className="h-5 w-5" />
        </Button>
      </SheetTrigger>
      
      <SheetContent 
        side="right" 
        className="w-full sm:w-[480px] bg-slate-950 border-white/10"
        data-testid="executive-assist-drawer"
      >
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2 text-slate-100">
            <Sparkles className="h-5 w-5 text-purple-400" />
            ExecutiveAssist Insights
          </SheetTitle>
          <SheetDescription className="text-slate-400">
            AI-powered analysis of your current analytics view
          </SheetDescription>
        </SheetHeader>
        
        <div className="mt-4 space-y-1 text-xs text-slate-400">
          <div className="flex items-center gap-2">
            <Badge variant="outline" className="text-xs border-slate-600 text-slate-300 bg-slate-800/50">
              {period.toUpperCase()}
            </Badge>
            {vertical && vertical !== "all" && (
              <Badge variant="outline" className="text-xs bg-emerald-500/20 text-emerald-300 border-emerald-500/30">
                {vertical === "mortgage_protection" ? "MP" : vertical}
              </Badge>
            )}
            {metaContext && (
              <Badge variant="outline" className="text-xs bg-blue-500/20 text-blue-300 border-blue-500/30">
                Meta: {metaContext.level}
              </Badge>
            )}
          </div>
        </div>

        <div className="mt-4 flex justify-end">
          <Button
            variant="outline"
            size="sm"
            onClick={generateInsights}
            disabled={loading}
            className="border-slate-600 text-slate-200 hover:bg-slate-800"
          >
            <RefreshCw className={`h-4 w-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            {loading ? 'Analyzing...' : 'Regenerate'}
          </Button>
        </div>

        <ScrollArea className="h-[calc(100vh-200px)] mt-4 pr-4">
          {loading && !insights && (
            <div className="space-y-4" data-testid="insights-loading">
              <Skeleton className="h-24 w-full" />
              <Skeleton className="h-32 w-full" />
              <Skeleton className="h-32 w-full" />
              <Skeleton className="h-40 w-full" />
            </div>
          )}

          {error && !loading && (
            <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-4 text-sm text-red-300">
              <AlertTriangle className="h-4 w-4 inline mr-2" />
              {error}
            </div>
          )}

          {insights && (
            <div className="space-y-6" data-testid="insights-content">
              {/* Executive Summary */}
              <section>
                <h3 className="text-sm font-semibold text-slate-200 flex items-center gap-2 mb-3">
                  <Target className="h-4 w-4 text-indigo-400" />
                  Executive Summary
                </h3>
                <div className="space-y-2">
                  {insights.executive_summary?.map((bullet, idx) => (
                    <div 
                      key={idx}
                      className="flex items-start gap-2 text-sm text-slate-300 bg-white/5 rounded-lg p-3"
                    >
                      <CheckCircle2 className="h-4 w-4 text-emerald-400 mt-0.5 shrink-0" />
                      <span>{bullet}</span>
                    </div>
                  ))}
                </div>
              </section>

              {/* Risks */}
              <Collapsible defaultOpen>
                <CollapsibleTrigger className="flex items-center justify-between w-full">
                  <h3 className="text-sm font-semibold text-slate-200 flex items-center gap-2">
                    <Shield className="h-4 w-4 text-red-400" />
                    Risks
                    <Badge variant="outline" className="text-xs border-slate-600 text-slate-300 bg-slate-800/50">
                      {insights.risks?.length || 0}
                    </Badge>
                  </h3>
                  <ChevronDown className="h-4 w-4 text-slate-300" />
                </CollapsibleTrigger>
                <CollapsibleContent className="mt-3 space-y-2">
                  {insights.risks?.length === 0 ? (
                    <p className="text-sm text-slate-500 italic">No significant risks identified</p>
                  ) : (
                    insights.risks?.map((risk, idx) => (
                      <div 
                        key={idx}
                        className="border border-white/10 rounded-lg p-3 bg-white/5"
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <Badge className={`text-xs ${getSeverityColor(risk.severity)}`}>
                            {risk.severity?.toUpperCase()}
                          </Badge>
                          {risk.affected_kpi && (
                            <span className="text-xs text-slate-500">
                              Affects: {risk.affected_kpi}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-slate-300">{risk.description}</p>
                      </div>
                    ))
                  )}
                </CollapsibleContent>
              </Collapsible>

              {/* Opportunities */}
              <Collapsible defaultOpen>
                <CollapsibleTrigger className="flex items-center justify-between w-full">
                  <h3 className="text-sm font-semibold text-slate-200 flex items-center gap-2">
                    <TrendingUp className="h-4 w-4 text-emerald-400" />
                    Opportunities
                    <Badge variant="outline" className="text-xs border-slate-600 text-slate-300 bg-slate-800/50">
                      {insights.opportunities?.length || 0}
                    </Badge>
                  </h3>
                  <ChevronDown className="h-4 w-4 text-slate-300" />
                </CollapsibleTrigger>
                <CollapsibleContent className="mt-3 space-y-2">
                  {insights.opportunities?.length === 0 ? (
                    <p className="text-sm text-slate-500 italic">No opportunities identified</p>
                  ) : (
                    insights.opportunities?.map((opp, idx) => (
                      <div 
                        key={idx}
                        className="border border-emerald-500/20 rounded-lg p-3 bg-emerald-500/5"
                      >
                        <p className="text-sm text-slate-300">{opp.description}</p>
                        {opp.potential_impact && (
                          <p className="text-xs text-emerald-400 mt-2">
                            Potential Impact: {opp.potential_impact}
                          </p>
                        )}
                      </div>
                    ))
                  )}
                </CollapsibleContent>
              </Collapsible>

              {/* Recommended Actions */}
              <Collapsible defaultOpen>
                <CollapsibleTrigger className="flex items-center justify-between w-full">
                  <h3 className="text-sm font-semibold text-slate-200 flex items-center gap-2">
                    <Zap className="h-4 w-4 text-yellow-400" />
                    Recommended Actions
                    <Badge variant="outline" className="text-xs border-slate-600 text-slate-300 bg-slate-800/50">
                      {insights.recommended_actions?.length || 0}
                    </Badge>
                  </h3>
                  <ChevronDown className="h-4 w-4 text-slate-300" />
                </CollapsibleTrigger>
                <CollapsibleContent className="mt-3 space-y-2">
                  {insights.recommended_actions?.length === 0 ? (
                    <p className="text-sm text-slate-500 italic">No actions recommended</p>
                  ) : (
                    insights.recommended_actions?.map((action, idx) => (
                      <div 
                        key={idx}
                        className="border border-yellow-500/20 rounded-lg p-3 bg-yellow-500/5"
                      >
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-xs font-bold text-yellow-400">
                            #{action.rank || idx + 1}
                          </span>
                          {action.priority && (
                            <Badge className={`text-xs ${getPriorityColor(action.priority)}`}>
                              {action.priority}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-slate-300">{action.action}</p>
                        {action.expected_impact && (
                          <p className="text-xs text-yellow-400 mt-2">
                            Expected Impact: {action.expected_impact}
                          </p>
                        )}
                      </div>
                    ))
                  )}
                </CollapsibleContent>
              </Collapsible>

              {/* Confidence Notes */}
              <Collapsible>
                <CollapsibleTrigger className="flex items-center justify-between w-full">
                  <h3 className="text-sm font-semibold text-slate-200 flex items-center gap-2">
                    <Info className="h-4 w-4 text-slate-300" />
                    Confidence Notes
                  </h3>
                  <ChevronDown className="h-4 w-4 text-slate-300" />
                </CollapsibleTrigger>
                <CollapsibleContent className="mt-3">
                  <div className="border border-white/10 rounded-lg p-3 bg-white/5 text-xs text-slate-400 space-y-2">
                    {insights.confidence_notes?.sample_size_concerns?.length > 0 && (
                      <div>
                        <span className="font-medium text-slate-300">Sample Size:</span>
                        <ul className="list-disc list-inside mt-1">
                          {insights.confidence_notes.sample_size_concerns.map((note, idx) => (
                            <li key={idx}>{note}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {insights.confidence_notes?.data_quality_notes?.length > 0 && (
                      <div>
                        <span className="font-medium text-slate-300">Data Quality:</span>
                        <ul className="list-disc list-inside mt-1">
                          {insights.confidence_notes.data_quality_notes.map((note, idx) => (
                            <li key={idx}>{note}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {(!insights.confidence_notes?.sample_size_concerns?.length && 
                      !insights.confidence_notes?.data_quality_notes?.length) && (
                      <p>No specific confidence concerns noted.</p>
                    )}
                  </div>
                </CollapsibleContent>
              </Collapsible>

              {/* Generated At */}
              <div className="text-xs text-slate-500 text-center pt-4 border-t border-white/10">
                Generated at {new Date(insights.generated_at).toLocaleString()}
              </div>
            </div>
          )}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};

export default ExecutiveAssistDrawer;
